import type { Node, NodeType } from "@gelatos/protocol";
import { classes, setAttr, setClassName, setText } from "../lib/dom.js";
import { createIcon, updateIcon } from "./icon.js";
import { bool, num, oneOf, optNum, optStr, options, space, str } from "./props.js";
import type { SurfaceFactory, SurfaceImpl } from "./surfaces.js";

/** How a rendered node reports back to its app (§5.2 `event`). */
export type Emit = (key: string, event: string, payload?: unknown) => void;

const AXES = ["row", "column"] as const;
const ALIGNS = ["start", "center", "end", "stretch", "baseline"] as const;
const JUSTIFIES = ["start", "center", "end", "between"] as const;
const TONES = ["default", "muted", "accent", "danger", "success"] as const;
const SIZES = ["xs", "sm", "md", "lg"] as const;
const BACKGROUNDS = ["none", "subtle", "raised"] as const;
const VARIANTS = ["default", "primary", "ghost", "danger"] as const;

const FLEX_ALIGN: Record<(typeof ALIGNS)[number], string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
  baseline: "baseline",
};

const FLEX_JUSTIFY: Record<(typeof JUSTIFIES)[number], string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
};

const TONE_CLASS: Record<string, string> = {
  muted: "tone-muted",
  accent: "tone-accent",
  danger: "tone-danger",
  success: "tone-success",
};

const SIZE_CLASS: Record<string, string> = {
  xs: "size-xs",
  sm: "size-sm",
  lg: "size-lg",
};

/**
 * Which DOM events each node type turns into which protocol event. Handled by
 * one delegated listener per window rather than a listener per element, so a
 * tree of a thousand nodes still costs five listeners.
 */
const EVENTS: Record<string, Record<string, string>> = {
  Button: { click: "press" },
  ListItem: { click: "select", dblclick: "activate" },
  ObjectRef: { dblclick: "activate" },
  TextField: { input: "input" },
  TextArea: { input: "input" },
  Checkbox: { change: "change" },
  Select: { change: "change" },
};

const DELEGATED = ["click", "dblclick", "input", "change", "keydown", "dragstart"] as const;

/**
 * What a dragged `ObjectRef` puts on the clipboard: a capability *id* and a
 * label, and nothing else (§5.1).
 *
 * The content is deliberately absent. Dropping this on another window makes the
 * shell mint a fresh port to the same object for the receiving app — a handoff
 * of authority, not a copy of data. Two apps end up looking at one object, so
 * an edit in either is visible in the other.
 */
export const CAPABILITY_MIME = "application/x-gelatos-capability";

/* ------------------------------------------------------------- instances */

type Slot = { host: HTMLElement; instances: Map<string, Instance> };

type Instance = {
  key: string;
  type: NodeType;
  el: HTMLElement | SVGSVGElement;
  /** Where this node's children are reconciled. Empty for leaf types. */
  slots: Slot[];
  parts: Record<string, HTMLElement | SVGSVGElement>;
  /**
   * The last value this input reported to the app. While the app's value equals
   * it, the DOM is left alone — that is what keeps the caret where the person
   * put it, and what stops a round trip through the Worker from being visible.
   */
  echo: string | null;
  /** A `Surface`'s live implementation, owned by the shell (§11). */
  surface: SurfaceImpl | null;
  /** The last props seen, so a deferred surface can be built with them. */
  surfaceProps: Record<string, unknown> | null;
};

function slot(host: HTMLElement): Slot {
  return { host, instances: new Map() };
}

function div(className: string): HTMLDivElement {
  const node = document.createElement("div");
  node.className = className;
  return node;
}

function span(className: string): HTMLSpanElement {
  const node = document.createElement("span");
  node.className = className;
  return node;
}

/* -------------------------------------------------------------- renderer */

export class TreeRenderer {
  readonly #scope: ShadowRoot;
  readonly #root: Slot;
  readonly #emit: Emit;
  readonly #byElement = new WeakMap<Element, Instance>();
  readonly #listeners: [string, EventListener][] = [];
  readonly #surfaces: SurfaceFactory | null;
  /** Surfaces waiting to be built, once their element is in the document. */
  readonly #unbuilt: Instance[] = [];

  constructor(scope: ShadowRoot, host: HTMLElement, emit: Emit, surfaces?: SurfaceFactory) {
    this.#scope = scope;
    this.#root = slot(host);
    this.#emit = emit;
    this.#surfaces = surfaces ?? null;

    for (const type of DELEGATED) {
      const listener = (event: Event): void => this.#dispatch(type, event);
      scope.addEventListener(type, listener);
      this.#listeners.push([type, listener]);
    }
  }

  render(tree: Node): void {
    // A render pass can move elements between parents. Chrome drops focus when
    // a focused element is detached, so the caret is saved across the pass and
    // put back if it moved.
    const focus = captureFocus(this.#scope);
    this.#reconcile(this.#root, [tree]);
    // Only now is the tree attached. A surface must not be built before then:
    // xterm and anything like it measures its container on open, and one that
    // opens into a detached element renders nothing at all, silently.
    this.#buildSurfaces();
    focus.restore();
  }

  clear(): void {
    this.#reconcile(this.#root, []);
  }

  destroy(): void {
    for (const [type, listener] of this.#listeners) {
      this.#scope.removeEventListener(type, listener);
    }
    this.#listeners.length = 0;
    this.clear();
  }

  #reconcile(target: Slot, nodes: readonly Node[]): void {
    const next = new Map<string, Instance>();

    for (const node of nodes) {
      let instance = target.instances.get(node.key);
      if (instance !== undefined && instance.type !== node.type) {
        // Same key, different type: there is nothing to carry over.
        this.#remove(instance);
        instance = undefined;
      }
      instance ??= this.#create(node);
      this.#patch(instance, node);
      next.set(node.key, instance);
    }

    for (const [key, instance] of target.instances) {
      if (!next.has(key)) this.#remove(instance);
    }
    target.instances = next;

    // Order pass: at most one move per node, and only for nodes that moved.
    let anchor: ChildNode | null = target.host.firstChild;
    for (const node of nodes) {
      const instance = next.get(node.key);
      if (instance === undefined) continue;
      if (instance.el === anchor) anchor = anchor.nextSibling;
      else target.host.insertBefore(instance.el, anchor);
    }
  }

  /**
   * Take an instance out of the tree, and everything it contained with it.
   *
   * Recursive because a `Surface` is a leaf: removing an ancestor would
   * otherwise leave a live terminal attached to a detached element, holding a
   * socket open — a leak that only shows up as a Sprite that never sleeps.
   */
  #remove(instance: Instance): void {
    instance.el.remove();
    this.#byElement.delete(instance.el);
    this.#dispose(instance);
  }

  #dispose(instance: Instance): void {
    const queued = this.#unbuilt.indexOf(instance);
    if (queued >= 0) this.#unbuilt.splice(queued, 1);
    instance.surface?.destroy();
    instance.surface = null;
    for (const target of instance.slots) {
      for (const child of target.instances.values()) this.#dispose(child);
      target.instances.clear();
    }
  }

  #create(node: Node): Instance {
    const instance = build(node.type);
    instance.key = node.key;
    instance.el.setAttribute("data-node", node.type);
    instance.el.setAttribute("data-key", node.key);
    this.#byElement.set(instance.el, instance);
    return instance;
  }

  #patch(instance: Instance, node: Node): void {
    patchProps(instance, node);
    const children = node.children ?? [];
    if (instance.slots.length === 1) {
      const target = instance.slots[0];
      if (target !== undefined) this.#reconcile(target, children);
    } else if (instance.slots.length > 1) {
      // Split: one child per pane.
      instance.slots.forEach((target, index) => {
        const child = children[index];
        this.#reconcile(target, child === undefined ? [] : [child]);
      });
    }
    if (instance.type === "Surface") this.#patchSurface(instance, node);
    if (instance.type === "ListItem") {
      const hasChildren = children.length > 0;
      setAttr(instance.parts["label"] as HTMLElement, "hidden", hasChildren ? "" : null);
      setAttr(instance.parts["content"] as HTMLElement, "hidden", hasChildren ? null : "");
    }
  }

  #patchSurface(instance: Instance, node: Node): void {
    instance.surfaceProps = node.props;
    if (instance.surface === null) {
      if (!this.#unbuilt.includes(instance)) this.#unbuilt.push(instance);
      return;
    }
    instance.surface.update(node.props);
  }

  #buildSurfaces(): void {
    // Anything still detached stays queued rather than being dropped: it may be
    // a node the next pass attaches.
    const waiting = this.#unbuilt.splice(0);
    for (const instance of waiting) {
      if (instance.surface !== null) continue;
      const body = instance.parts["body"] as HTMLElement;
      if (!body.isConnected) {
        if (instance.el.isConnected || this.#byElement.has(instance.el)) {
          this.#unbuilt.push(instance);
        }
        continue;
      }

      const props = instance.surfaceProps ?? {};
      const kind = str(props, "kind");

      // Empty the host *before* handing it over. The implementation builds its
      // own DOM in here, and clearing afterwards would delete it — which is
      // exactly what happened: a terminal that opened correctly and was then
      // wiped by the line meant to remove a placeholder.
      setText(body, "");
      instance.surface = this.#surfaces?.(kind, body) ?? null;

      // A kind nobody implements renders as a labelled hole rather than
      // nothing, so an unimplemented surface is visible instead of silent.
      setAttr(body, "class", instance.surface === null ? "surface-unknown" : "surface-body");
      if (instance.surface === null) {
        setText(body, `no surface implementation for kind \u201c${kind}\u201d`);
        continue;
      }
      instance.surface.update(props);
    }
  }

  #dispatch(type: string, event: Event): void {
    for (const target of event.composedPath()) {
      if (target === this.#scope) return;
      if (!(target instanceof Element)) continue;
      const nodeType = target.getAttribute("data-node");
      if (nodeType === null) continue;
      const key = target.getAttribute("data-key");
      if (key === null) continue;
      const instance = this.#byElement.get(target);

      if (type === "dragstart") {
        if (nodeType !== "ObjectRef") continue;
        const transfer = (event as DragEvent).dataTransfer;
        if (transfer === null) return;
        transfer.setData(
          CAPABILITY_MIME,
          JSON.stringify({
            capability: target.getAttribute("data-capability") ?? "",
            objectType: target.getAttribute("data-object-type") ?? "",
            label: target.textContent ?? "",
          }),
        );
        transfer.effectAllowed = "link";
        return;
      }

      if (type === "keydown") {
        if (nodeType !== "TextField") continue;
        const keyboard = event as KeyboardEvent;
        if (keyboard.key !== "Enter") return;
        const input = instance?.parts["input"];
        this.#emit(key, "submit", {
          value: input instanceof HTMLInputElement ? input.value : "",
        });
        return;
      }

      const name = EVENTS[nodeType]?.[type];
      if (name === undefined) continue;
      this.#emit(key, name, this.#payload(nodeType, instance));
      return;
    }
  }

  #payload(nodeType: string, instance: Instance | undefined): unknown {
    if (instance === undefined) return undefined;
    switch (nodeType) {
      case "TextField":
      case "TextArea": {
        const field = instance.parts["input"];
        if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLTextAreaElement)) {
          return undefined;
        }
        instance.echo = field.value;
        return { value: field.value };
      }
      case "Checkbox": {
        const box = instance.parts["input"];
        if (!(box instanceof HTMLInputElement)) return undefined;
        instance.echo = String(box.checked);
        return { checked: box.checked };
      }
      case "Select": {
        const select = instance.parts["input"];
        if (!(select instanceof HTMLSelectElement)) return undefined;
        instance.echo = select.value;
        return { value: select.value };
      }
      default:
        return undefined;
    }
  }
}

/* ------------------------------------------------------------ focus rules */

function textSelection(node: Element): { start: number; end: number } | null {
  if (!(node instanceof HTMLInputElement) && !(node instanceof HTMLTextAreaElement)) return null;
  try {
    const { selectionStart, selectionEnd } = node;
    if (selectionStart === null || selectionEnd === null) return null;
    return { start: selectionStart, end: selectionEnd };
  } catch {
    // Input types such as checkbox throw rather than reporting no selection.
    return null;
  }
}

function restoreSelection(node: Element, selection: { start: number; end: number } | null): void {
  if (selection === null) return;
  if (!(node instanceof HTMLInputElement) && !(node instanceof HTMLTextAreaElement)) return;
  const max = node.value.length;
  try {
    node.setSelectionRange(Math.min(selection.start, max), Math.min(selection.end, max));
  } catch {
    // Not selectable any more; the caret is the browser's problem now.
  }
}

function captureFocus(scope: ShadowRoot): { restore: () => void } {
  const active = scope.activeElement;
  if (!(active instanceof HTMLElement)) return { restore: () => undefined };
  const selection = textSelection(active);
  return {
    restore(): void {
      if (!active.isConnected || scope.activeElement === active) return;
      active.focus({ preventScroll: true });
      restoreSelection(active, selection);
    },
  };
}

/** Write a value without moving the caret unless the text really changed. */
function applyValue(field: HTMLInputElement | HTMLTextAreaElement, next: string): void {
  if (field.value === next) return;
  const selection = textSelection(field);
  field.value = next;
  const root = field.getRootNode();
  const focused = root instanceof ShadowRoot ? root.activeElement === field : false;
  if (focused) restoreSelection(field, selection);
}

/* -------------------------------------------------------------- creation */

function build(type: NodeType): Instance {
  const base = (el: HTMLElement | SVGSVGElement, slots: Slot[] = [], parts = {}): Instance => ({
    key: "",
    type,
    el,
    slots,
    parts,
    echo: null,
    surface: null,
    surfaceProps: null,
  });

  switch (type) {
    case "Box": {
      const node = div("box");
      return base(node, [slot(node)]);
    }
    case "Stack": {
      const node = div("stack");
      return base(node, [slot(node)]);
    }
    case "Split": {
      const node = div("split");
      const first = div("split-pane");
      const bar = div("split-bar");
      const second = div("split-pane");
      node.append(first, bar, second);
      return base(node, [slot(first), slot(second)], { bar });
    }
    case "Toolbar": {
      const node = div("toolbar");
      return base(node, [slot(node)]);
    }
    case "Divider": {
      const node = div("divider");
      node.setAttribute("role", "separator");
      return base(node);
    }
    case "Spacer":
      return base(div("spacer-flex"));
    case "Text":
      return base(span("text"));
    case "Heading": {
      // One element for all three levels: changing `level` must not replace the
      // node, or a heading could not hold focus or an animation across renders.
      const node = div("heading");
      node.setAttribute("role", "heading");
      return base(node);
    }
    case "Icon":
      return base(createIcon("app"));
    case "ObjectRef": {
      const node = document.createElement("button");
      node.className = "object-ref";
      node.type = "button";
      const icon = createIcon("object", 13, "muted");
      const label = span("object-ref-label");
      node.append(icon, label);
      return base(node, [], { icon, label });
    }
    case "Button": {
      const node = document.createElement("button");
      node.className = "button";
      node.type = "button";
      const icon = createIcon("app", 14);
      icon.classList.add("icon-slot");
      const label = span("");
      node.append(icon, label);
      return base(node, [], { icon, label });
    }
    case "TextField": {
      const node = document.createElement("label");
      node.className = "field";
      const label = span("field-label");
      const input = document.createElement("input");
      input.className = "input";
      node.append(label, input);
      return base(node, [], { label, input });
    }
    case "TextArea": {
      const node = document.createElement("label");
      node.className = "field grow";
      const label = span("field-label");
      const input = document.createElement("textarea");
      input.className = "input textarea grow";
      node.append(label, input);
      return base(node, [], { label, input });
    }
    case "Checkbox": {
      const node = document.createElement("label");
      node.className = "checkbox";
      const input = document.createElement("input");
      input.type = "checkbox";
      const label = span("");
      node.append(input, label);
      return base(node, [], { input, label });
    }
    case "Select": {
      const node = document.createElement("label");
      node.className = "field";
      const label = span("field-label");
      const input = document.createElement("select");
      input.className = "input";
      node.append(label, input);
      return base(node, [], { label, input });
    }
    case "List": {
      const node = div("list");
      node.setAttribute("role", "list");
      return base(node, [slot(node)]);
    }
    case "ListItem": {
      const node = document.createElement("button");
      node.className = "list-item";
      node.type = "button";
      node.setAttribute("role", "listitem");
      const icon = createIcon("document", 14, "muted");
      icon.classList.add("icon-slot");
      const label = span("list-item-label");
      const content = span("list-item-content");
      const detail = span("list-item-detail");
      node.append(icon, label, content, detail);
      return base(node, [slot(content)], { icon, label, content, detail });
    }
    case "Surface": {
      const node = div("surface");
      const body = div("surface-unknown");
      node.appendChild(body);
      return base(node, [], { body });
    }
  }
}

/* ----------------------------------------------------------- prop patching */

function patchProps(instance: Instance, node: Node): void {
  const p = node.props;
  const el = instance.el;

  switch (instance.type) {
    case "Box": {
      const background = oneOf(p, "background", BACKGROUNDS, "none");
      const gap = optNum(p, "gap");
      const radius = optNum(p, "radius");
      setClassName(
        el,
        classes(
          "box",
          bool(p, "grow") && "grow",
          bool(p, "scroll") && "scroll",
          background === "subtle" && "bg-subtle",
          background === "raised" && "bg-raised",
          bool(p, "border") && "bordered",
        ),
      );
      const style = (el as HTMLElement).style;
      style.padding = px(space(p, "padding"));
      style.display = gap === undefined ? "" : "flex";
      style.flexDirection = gap === undefined ? "" : "column";
      style.gap = gap === undefined ? "" : px(gap);
      style.borderRadius = radius === undefined ? "" : px(radius);
      return;
    }
    case "Stack": {
      const direction = oneOf(p, "direction", AXES, "column");
      setClassName(
        el,
        classes("stack", bool(p, "grow") && "grow", bool(p, "scroll") && "scroll"),
      );
      setAttr(el, "data-direction", direction);
      const style = (el as HTMLElement).style;
      style.flexDirection = direction;
      style.gap = px(space(p, "gap"));
      style.padding = px(space(p, "padding"));
      style.alignItems = FLEX_ALIGN[oneOf(p, "align", ALIGNS, "stretch")];
      style.justifyContent = FLEX_JUSTIFY[oneOf(p, "justify", JUSTIFIES, "start")];
      style.flexWrap = bool(p, "wrap") ? "wrap" : "nowrap";
      return;
    }
    case "Split": {
      const direction = oneOf(p, "direction", AXES, "row");
      const ratio = Math.max(0.05, Math.min(0.95, num(p, "ratio", 0.5)));
      (el as HTMLElement).style.flexDirection = direction;
      setAttr(instance.parts["bar"] as HTMLElement, "data-direction", direction);
      const [first, second] = instance.slots;
      // Longhands, not the `flex` shorthand: shorthand serialisation varies
      // between CSSOM implementations and the panes silently get no style.
      setFlex(first?.host, ratio, 1, "0");
      setFlex(second?.host, 1 - ratio, 1, "0");
      return;
    }
    case "Toolbar":
      (el as HTMLElement).style.gap = px(space(p, "gap", 6));
      return;
    case "Divider":
      setAttr(el, "data-direction", oneOf(p, "direction", AXES, "column"));
      return;
    case "Spacer": {
      const size = optNum(p, "size");
      setClassName(el, size === undefined ? "spacer-flex" : "");
      const style = (el as HTMLElement).style;
      if (size === undefined) setFlex(el as HTMLElement, null, null, "");
      else setFlex(el as HTMLElement, 0, 0, "auto");
      style.width = size === undefined ? "" : px(size);
      style.height = size === undefined ? "" : px(size);
      return;
    }
    case "Text": {
      setClassName(
        el,
        classes(
          "text",
          TONE_CLASS[oneOf(p, "tone", TONES, "default")],
          SIZE_CLASS[oneOf(p, "size", SIZES, "md")],
          bool(p, "mono") && "mono",
          bool(p, "bold") && "bold",
        ),
      );
      setAttr(el, "data-wrap", String(bool(p, "wrap", true)));
      setText(el, str(p, "text"));
      return;
    }
    case "Heading": {
      const level = Math.min(3, Math.max(1, Math.round(num(p, "level", 2))));
      setAttr(el, "data-level", String(level));
      setAttr(el, "aria-level", String(level));
      setText(el, str(p, "text"));
      return;
    }
    case "Icon":
      updateIcon(
        el as SVGSVGElement,
        str(p, "name"),
        num(p, "size", 16),
        oneOf(p, "tone", TONES, "default"),
      );
      return;
    case "ObjectRef": {
      const label = str(p, "label");
      const objectType = str(p, "objectType");
      setAttr(el, "data-object-type", objectType);
      setAttr(el, "data-capability", str(p, "capability"));
      setAttr(el, "title", `${label} · ${objectType}`);
      setAttr(el, "draggable", "true");
      updateIcon(instance.parts["icon"] as SVGSVGElement, str(p, "icon", "object"), 13, "muted");
      setText(instance.parts["label"] as HTMLElement, label);
      return;
    }
    case "Button": {
      const icon = optStr(p, "icon");
      setAttr(el, "data-variant", oneOf(p, "variant", VARIANTS, "default"));
      (el as HTMLButtonElement).disabled = bool(p, "disabled");
      const iconEl = instance.parts["icon"] as SVGSVGElement;
      setAttr(iconEl, "hidden", icon === undefined ? "" : null);
      if (icon !== undefined) updateIcon(iconEl, icon, 14);
      setText(instance.parts["label"] as HTMLElement, str(p, "label"));
      return;
    }
    case "TextField": {
      const input = instance.parts["input"] as HTMLInputElement;
      const label = optStr(p, "label");
      setAttr(instance.parts["label"] as HTMLElement, "hidden", label === undefined ? "" : null);
      setText(instance.parts["label"] as HTMLElement, label ?? "");
      setClassName(input, classes("input", bool(p, "mono") && "mono"));
      setAttr(input, "placeholder", str(p, "placeholder"));
      input.disabled = bool(p, "disabled");
      const value = str(p, "value");
      if (value !== instance.echo) {
        applyValue(input, value);
        instance.echo = value;
      }
      return;
    }
    case "TextArea": {
      const input = instance.parts["input"] as HTMLTextAreaElement;
      const label = optStr(p, "label");
      setAttr(instance.parts["label"] as HTMLElement, "hidden", label === undefined ? "" : null);
      setText(instance.parts["label"] as HTMLElement, label ?? "");
      setClassName(input, classes("input", "textarea", "grow", bool(p, "mono") && "mono"));
      setAttr(input, "placeholder", str(p, "placeholder"));
      setAttr(input, "rows", String(num(p, "rows", 4)));
      input.disabled = bool(p, "disabled");
      const value = str(p, "value");
      if (value !== instance.echo) {
        applyValue(input, value);
        instance.echo = value;
      }
      return;
    }
    case "Checkbox": {
      const input = instance.parts["input"] as HTMLInputElement;
      input.disabled = bool(p, "disabled");
      setText(instance.parts["label"] as HTMLElement, str(p, "label"));
      const checked = String(bool(p, "checked"));
      if (checked !== instance.echo) {
        input.checked = checked === "true";
        instance.echo = checked;
      }
      return;
    }
    case "Select": {
      const select = instance.parts["input"] as HTMLSelectElement;
      const label = optStr(p, "label");
      setAttr(instance.parts["label"] as HTMLElement, "hidden", label === undefined ? "" : null);
      setText(instance.parts["label"] as HTMLElement, label ?? "");
      select.disabled = bool(p, "disabled");

      const list = options(p, "options");
      const signature = list.map((option) => `${option.value} ${option.label}`).join("");
      if (select.getAttribute("data-options") !== signature) {
        setAttr(select, "data-options", signature);
        select.replaceChildren(
          ...list.map((option) => {
            const node = document.createElement("option");
            node.value = option.value;
            node.textContent = option.label;
            return node;
          }),
        );
        // Options were replaced, so whatever the element held is gone.
        instance.echo = null;
      }
      const value = str(p, "value");
      if (value !== instance.echo) {
        select.value = value;
        instance.echo = value;
      }
      return;
    }
    case "List":
      setClassName(
        el,
        classes("list", bool(p, "grow") && "grow", bool(p, "scroll") && "scroll"),
      );
      setAttr(el, "data-selected-key", optStr(p, "selectedKey") ?? null);
      return;
    case "ListItem": {
      const icon = optStr(p, "icon");
      const detail = optStr(p, "detail");
      setAttr(el, "data-selected", String(bool(p, "selected")));
      (el as HTMLButtonElement).disabled = bool(p, "disabled");
      const iconEl = instance.parts["icon"] as SVGSVGElement;
      setAttr(iconEl, "hidden", icon === undefined ? "" : null);
      if (icon !== undefined) updateIcon(iconEl, icon, 14, "muted");
      setText(instance.parts["label"] as HTMLElement, str(p, "label"));
      const detailEl = instance.parts["detail"] as HTMLElement;
      setAttr(detailEl, "hidden", detail === undefined ? "" : null);
      setText(detailEl, detail ?? "");
      return;
    }
    case "Surface":
      // Only the frame. What is *inside* belongs to the surface implementation
      // (§11) and is wired up in #patchSurface, where it has a lifecycle.
      //
      // Nothing here may touch the body element. A surface owns that subtree —
      // xterm builds its own DOM in it — and writing to it on a later render
      // would silently destroy a live terminal while leaving its socket open.
      setAttr(el, "data-kind", str(p, "kind"));
      return;
  }
}

function px(value: number): string {
  return value === 0 ? "" : `${value}px`;
}

function setFlex(
  node: HTMLElement | undefined,
  grow: number | null,
  shrink: number | null,
  basis: string,
): void {
  if (node === undefined) return;
  node.style.flexGrow = grow === null ? "" : String(grow);
  node.style.flexShrink = shrink === null ? "" : String(shrink);
  node.style.flexBasis = basis;
}
